import { useState, useEffect, useCallback } from "react";
import { ArrowRight, BookOpen } from "lucide-react";
import "./category-hero.less";

/**
 * CategoryHero -- Academy landing hero section
 *
 * Left: slogan + CTA buttons
 * Right: auto-rotating image carousel with 4 hero illustrations
 */

const HERO_IMAGES = [
  `${import.meta.env.BASE_URL}images/academy/hero-illustration.png`,
  `${import.meta.env.BASE_URL}images/academy/hero-2.png`,
  `${import.meta.env.BASE_URL}images/academy/hero-3.png`,
  `${import.meta.env.BASE_URL}images/academy/hero-4.png`,
];

const CAROUSEL_INTERVAL = 4000; // ms

interface CategoryHeroProps {
  onBrowseCourses?: () => void;
}

export function CategoryHero({ onBrowseCourses }: CategoryHeroProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  const next = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % HERO_IMAGES.length);
  }, []);

  // Auto-rotate
  useEffect(() => {
    const timer = setInterval(next, CAROUSEL_INTERVAL);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <section className="category-hero">
      {/* ── Decorative background elements ── */}
      <div className="category-hero__deco-skew" aria-hidden="true" />
      <div className="category-hero__deco-blur" aria-hidden="true" />

      <div className="category-hero__inner">
        <div className="category-hero__grid">
          {/* ── Left: copy ── */}
          <div className="category-hero__copy">
            {/* Badge pill */}
            <span className="category-hero__badge">
              <BookOpen className="category-hero__badge-icon" />
              在线学习平台
            </span>

            {/* Heading */}
            <h1 className="category-hero__heading">
              SafeClaw{" "}
              <span className="category-hero__heading-accent">学院</span>
            </h1>

            <p className="category-hero__subtitle">掌握 AI 时代的核心技能</p>

            <p className="category-hero__desc">
              从提示词工程到多智能体协作，通过互动课程与实战项目，系统化提升你的
              AI 应用能力，解锁 10 倍效率工作方式。
            </p>

            {/* CTA buttons */}
            <div className="category-hero__cta-group">
              <button
                type="button"
                onClick={onBrowseCourses}
                className="category-hero__cta-primary"
              >
                浏览全部课程
                <ArrowRight className="category-hero__cta-primary-icon" />
              </button>
            </div>
          </div>

          {/* ── Right: image carousel ── */}
          <div className="category-hero__visual">
            <div className="category-hero__image-box">
              <div className="category-hero__carousel">
                {HERO_IMAGES.map((src, i) => (
                  <img
                    key={src}
                    src={src}
                    alt={`学院插画 ${i + 1}`}
                    className={`category-hero__carousel-img ${
                      i === activeIndex ? "category-hero__carousel-img--active" : ""
                    }`}
                  />
                ))}
              </div>

              {/* Dot indicators */}
              <div className="category-hero__carousel-dots">
                {HERO_IMAGES.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`切换到第 ${i + 1} 张图片`}
                    className={`category-hero__carousel-dot ${
                      i === activeIndex ? "category-hero__carousel-dot--active" : ""
                    }`}
                    onClick={() => setActiveIndex(i)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
